import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  getShifts,
  createShiftWithRules,
  updateShiftWithRules,
  deleteShift,
  getProjects,
  getVenues,
  getVenueLocations,
  recordShiftRuleAcceptances,
  extractErrorMessage,
} from "../lib/api";
import {
  PageHeader,
  DataTable,
  StatusBadge,
  LoadingSpinner,
  EmptyState,
  ErrorState,
} from "../components/shared/DataComponents";
import { useFormatters } from "../components/shared/useFormatters";
import { useShiftDuration } from "../hooks/useShiftDuration";
import { RulePromptDialog } from "../components/shared/RulePromptDialog";
import { DestructiveConfirmDialog } from "../components/shared/DestructiveConfirmDialog";
import { ShiftCrewAssignment } from "../components/shifts/ShiftCrewAssignment";
import ShiftRuleStatusBadge from "../components/shifts/ShiftRuleStatusBadge";
import { Button } from "../components/ui/button";
// EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Clock, Pencil, Trash2, ShieldAlert, Loader2, Timer, Sparkles, Users, MapPin, X, Info, Eye, EyeOff } from "lucide-react";
import { cn } from "../lib/utils";
import { Switch } from "../components/ui/switch";
import { ShiftLineItems } from "../components/shifts/ShiftLineItems";
import { useTableDisplaySettings, getZoomClasses } from "../hooks/useZoom";
import { ZoomControl } from "../components/common/ZoomControl";
import PageZoomContainer from "../components/layout/PageZoomContainer";

export default function ShiftsPage() {
  const navigate = useNavigate();
  const { formatDate, formatTime, formatDuration } = useFormatters();
  const [shifts, setShifts] = useState([]);
  // EVENTSFLO-SHIFT-RULE-VISIBILITY-AND-CREW-PLANNER-ENFORCEMENT-AUDIT-V1
  // Bumped after every rule-aware save so every visible badge re-fetches.
  const [shiftBadgeRefreshKey, setShiftBadgeRefreshKey] = useState(0);
  const [projects, setProjects] = useState([]);
  const [venues, setVenues] = useState([]);
  const [venueLocations, setVenueLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1
  const shiftLineItemsRef = useRef(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [discardInProgress, setDiscardInProgress] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  
  // EF-EMAIL-SUBJECT-ROUTER-V1 + UI-ZOOM-EXTENSION: Zoom and wrap settings
  const { 
    currentZoom, scale, zoomIn, zoomOut, canZoomIn, canZoomOut,
    wrapText, toggleWrap 
  } = useTableDisplaySettings("shifts-page");
  const zoomClasses = getZoomClasses(scale);
  
  // EF-DATA-CLEANUP-AND-SHIFT-VISIBILITY-V1: Hide Past Shifts toggle
  const [hidePastShifts, setHidePastShifts] = useState(() => {
    try {
      const stored = localStorage.getItem("hidePastShifts");
      return stored === null ? true : stored === "true";
    } catch { return true; }
  });
  
  // Rule prompt state
  const [rulePromptOpen, setRulePromptOpen] = useState(false);
  const [pendingShift, setPendingShift] = useState(null);
  const [rulePrompts, setRulePrompts] = useState([]);
  const [ruleLoading, setRuleLoading] = useState(false);
  
  // Rule notification state (shows after successful save with rules applied)
  const [ruleNotification, setRuleNotification] = useState(null);
  
  // Duration auto-calculation hook
  const {
    startTime,
    endTime,
    duration,
    setStartTime,
    setEndTime,
    setDuration,
    reset: resetDuration,
    isEndTimeCalculated,
    isDurationCalculated,
  } = useShiftDuration({ useDefaults: false });
  
  const [formData, setFormData] = useState({
    project_id: "",
    venue_id: "",
    venue_location_id: "",
    name: "",
    date: "",
    staff_required: 1,
    status: "draft",
    notes: "",
    out_of_town: false,
  });

  // EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: Server-side sorting/filtering state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [sortField, setSortField] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const PAGE_SIZE = 100;

  useEffect(() => {
    loadData();
  }, [sortField, sortDirection, searchTerm, filterStatus]);

  // EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: Use unified /api/shifts/list endpoint
  const loadData = async (page = 1, append = false) => {
    try {
      if (!append) {
        setLoading(true);
      }
      setError(false);
      
      // Build query params for unified endpoint
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', PAGE_SIZE.toString());
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      if (filterStatus && filterStatus !== 'all') {
        params.append('filter', `status:${filterStatus}`);
      }
      
      params.append('sort', `${sortField}:${sortDirection}`);
      
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('eventsflo_auth_token');
      
      const [shiftRes, projRes, venuesRes, venueLocsRes] = await Promise.all([
        fetch(`${API_URL}/api/shifts/list?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.json()),
        page === 1 ? getProjects() : Promise.resolve({ data: projects }),
        page === 1 ? getVenues() : Promise.resolve({ data: venues }),
        page === 1 ? getVenueLocations() : Promise.resolve({ data: venueLocations }),
      ]);
      
      // Handle unified response format
      const shiftsData = shiftRes.data || [];
      const meta = shiftRes.meta || { page: 1, limit: PAGE_SIZE, total: shiftsData.length, total_pages: 1 };
      
      if (append) {
        setShifts(prev => [...prev, ...shiftsData]);
      } else {
        setShifts(shiftsData);
      }
      
      setTotalCount(meta.total);
      setHasMore(meta.page < meta.total_pages);
      setCurrentPage(meta.page);
      
      if (page === 1) {
        setProjects(projRes.data);
        setVenues(venuesRes.data);
        setVenueLocations(venueLocsRes.data);
      }
    } catch (err) {
      console.error("Failed to load shifts:", err);
      setError(true);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: Server-side sort handler
  const handleServerSort = (field, direction) => {
    if (direction === null) {
      setSortField("date");
      setSortDirection("desc");
    } else {
      setSortField(field);
      setSortDirection(direction);
    }
  };

  // EF-DATA-CLEANUP-AND-SHIFT-VISIBILITY-V1: Handle hide past shifts toggle
  const handleToggleHidePastShifts = (checked) => {
    setHidePastShifts(checked);
    try {
      localStorage.setItem("hidePastShifts", String(checked));
    } catch { /* ignore */ }
  };

  // EF-DATA-CLEANUP-AND-SHIFT-VISIBILITY-V1: Filter shifts based on hidePastShifts setting
  const visibleShifts = React.useMemo(() => {
    if (!hidePastShifts) return shifts;
    
    const now = new Date();
    return shifts.filter(shift => {
      // Use end_time if available, otherwise check date
      if (shift.end_time) {
        return new Date(shift.end_time) >= now;
      }
      if (shift.date) {
        const shiftDate = new Date(shift.date);
        shiftDate.setHours(23, 59, 59, 999);
        return shiftDate >= now;
      }
      return true;
    });
  }, [shifts, hidePastShifts]);

  const getProjectName = (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    return project?.name || "Unknown";
  };

  const getVenueName = (venueId) => {
    const venue = venues.find((v) => v.id === venueId);
    return venue?.name || "Unknown";
  };
  
  // Get filtered venue locations for cascading dropdown
  const getFilteredVenueLocations = (venueId) => {
    if (!venueId) return [];
    return venueLocations.filter((vl) => vl.venue_id === venueId);
  };

  const handleOpenDialog = (shift = null) => {
    if (shift) {
      setSelectedShift(shift);
      setFormData({
        project_id: shift.project_id,
        venue_id: shift.venue_id || "",
        venue_location_id: shift.venue_location_id || "",
        name: shift.name,
        date: shift.date,
        staff_required: shift.staff_required,
        status: shift.status,
        notes: shift.notes || "",
        out_of_town: shift.out_of_town || false,
      });
      // Reset duration hook with shift times
      resetDuration(shift.start_time, shift.end_time);
    } else {
      setSelectedShift(null);
      setFormData({
        project_id: "",
        venue_id: "",
        venue_location_id: "",
        name: "",
        date: "",
        staff_required: 1,
        status: "draft",
        notes: "",
        out_of_town: false,
      });
      // Reset duration hook with default 8-hour shift
      resetDuration("09:00", "17:00");
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // EF-SHIFT-VALIDATION-AND-CREW-CHIEF-RULE-FIX-V1: Validate required fields before submission
    if (!formData.venue_id) {
      toast.error("Venue is required before saving the shift.");
      return;
    }
    if (!formData.venue_location_id) {
      toast.error("Location is required before saving the shift.");
      return;
    }
    if (!formData.date) {
      toast.error("Date is required before saving the shift.");
      return;
    }
    if (!startTime) {
      toast.error("Start time is required before saving the shift.");
      return;
    }
    if (!durationHours || durationHours <= 0) {
      toast.error("Duration (hours) is required before saving the shift.");
      return;
    }
    if (!formData.staff_required || parseInt(formData.staff_required, 10) < 1) {
      toast.error("Crew required must be at least 1.");
      return;
    }
    
    setSubmitting(true);
    try {
      // Combine form data with time values from duration hook
      const data = {
        ...formData,
        start_time: startTime,
        end_time: endTime,
        staff_required: parseInt(formData.staff_required, 10),
        out_of_town: formData.out_of_town,
      };
      
      // Use rule-aware endpoints
      let response;
      if (selectedShift) {
        response = await updateShiftWithRules(selectedShift.id, data);
      } else {
        response = await createShiftWithRules(data);
      }
      
      const result = response.data;
      
      // Check if there are rule prompts requiring user action
      if (result.requires_confirmation && result.rule_prompts?.length > 0) {
        // Store the shift and prompts, show rule dialog
        setPendingShift(result.shift);
        setRulePrompts(result.rule_prompts);
        setDialogOpen(false);
        setRulePromptOpen(true);
      } else {
        // No rules triggered OR rules auto-applied without confirmation needed
        const autoAppliedRules = result.rule_prompts?.filter(r => r.triggered && !r.requires_confirmation) || [];
        
        if (autoAppliedRules.length > 0) {
          // Show rule notification for auto-applied rules
          setRuleNotification({
            rules: autoAppliedRules,
            shiftName: formData.name || result.shift?.name || "Shift"
          });
        }
        
        toast.success(selectedShift ? "Shift updated successfully" : "Shift created successfully");
        // EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1
        try { shiftLineItemsRef.current?.commit?.(); } catch { /* no-op */ }
        setDialogOpen(false);
        loadData();
        // EVENTSFLO-SHIFT-RULE-VISIBILITY-AND-CREW-PLANNER-ENFORCEMENT-AUDIT-V1
        setShiftBadgeRefreshKey((k) => k + 1);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle accepting all rule prompts
  const handleRuleAcceptAll = async (acceptances) => {
    if (!pendingShift) return;
    
    setRuleLoading(true);
    try {
      await recordShiftRuleAcceptances(pendingShift.id, acceptances);
      toast.success("Shift saved with rules accepted");
      setRulePromptOpen(false);
      setPendingShift(null);
      setRulePrompts([]);
      loadData();
      setShiftBadgeRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setRuleLoading(false);
    }
  };

  // Handle overriding rule prompts
  const handleRuleOverride = async (acceptances, globalReason) => {
    if (!pendingShift) return;
    
    setRuleLoading(true);
    try {
      await recordShiftRuleAcceptances(pendingShift.id, acceptances);
      toast.success("Shift saved with rules overridden");
      setRulePromptOpen(false);
      setPendingShift(null);
      setRulePrompts([]);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setRuleLoading(false);
    }
  };

  // Handle canceling rule prompts (shift is already created, this just closes the dialog)
  const handleRuleCancel = () => {
    toast.info("Shift created - rule acknowledgment pending");
    setRulePromptOpen(false);
    setPendingShift(null);
    setRulePrompts([]);
    loadData();
  };

  const handleDelete = async (reason) => {
    setDeleting(true);
    try {
      await deleteShift(selectedShift.id, reason);
      toast.success("Shift deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedShift(null);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  // EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: Add sortable flags to columns
  const columns = [
    { 
      key: "name", 
      label: "Name",
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDialog(row);
            }}
            className="text-left font-medium text-primary hover:underline focus:outline-none"
            data-testid={`shift-name-link-${row.id}`}
          >
            {value}
          </button>
          {/* EVENTSFLO-SHIFT-RULE-VISIBILITY-AND-CREW-PLANNER-ENFORCEMENT-AUDIT-V1 */}
          <ShiftRuleStatusBadge
            shiftId={row.id}
            compact
            refreshKey={shiftBadgeRefreshKey}
          />
        </div>
      ),
    },
    {
      key: "project_id",
      label: "Project",
      render: (value) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/projects/${value}`);
          }}
          className="text-primary hover:underline focus:outline-none"
        >
          {getProjectName(value)}
        </button>
      ),
    },
    {
      key: "venue_id",
      label: "Venue",
      render: (value) => getVenueName(value),
    },
    {
      key: "date",
      label: "Date",
      sortable: true,
      render: (value) => formatDate(value),
    },
    {
      key: "time",
      label: "Time",
      render: (_, row) => `${row.start_time} - ${row.end_time}`,
    },
    {
      key: "duration_hours",
      label: "Duration",
      align: "right",
      render: (value) => <span className="font-mono">{value}h</span>,
    },
    {
      key: "staff_required",
      label: "Crew",
      align: "right",
      render: (value) => <span className="font-mono">{value}</span>,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (value) => <StatusBadge status={value} />,
    },
    {
      key: "actions",
      label: "",
      align: "right",
      render: (_, row) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/shifts/${row.id}/tasks`);
            }}
            title="Manage Tasks & Crew"
            data-testid={`manage-tasks-${row.id}`}
          >
            <Users className="h-4 w-4 text-teal-500" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDialog(row);
            }}
            data-testid={`edit-shift-${row.id}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedShift(row);
              setDeleteDialogOpen(true);
            }}
            data-testid={`delete-shift-${row.id}`}
          >
            <Trash2 className="h-4 w-4 text-red-400" />
          </Button>
        </div>
      ),
    },
  ];

  const canCreateShift = projects.length > 0 && venueLocations.length > 0;

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load shifts"
        message="We couldn't load the shifts data. This might be a temporary connection issue. Please try again."
        onRetry={loadData}
      />
    );
  }

  return (
    <div data-testid="shifts-page">
      {/* EF-GLOBAL-PAGE-ZOOM-FIX-V1: Wrap page content with zoom container */}
      <PageZoomContainer scale={scale}>
        <PageHeader
          title="Shifts"
          subtitle="Core execution units - schedule and manage shifts"
          actions={
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium tracking-wide uppercase text-xs px-6"
              data-testid="add-shift-btn"
              disabled={!canCreateShift}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Shift
            </Button>
          }
        />

        {/* Rule Notification Banner - Shows when rules auto-applied supplements */}
        {ruleNotification && (
          <div 
            className="mx-0 mb-4 p-4 rounded-lg border-l-4 border-primary bg-surface-elevated flex items-start justify-between gap-4"
            data-testid="rule-notification-banner"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-primary/10">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">
                  {ruleNotification.rules.length} Rule{ruleNotification.rules.length > 1 ? 's' : ''} Applied
                </h4>
                <ul className="space-y-1">
                  {ruleNotification.rules.map((rule, idx) => (
                    <li key={idx} className="text-sm text-muted-foreground flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                      <span className="font-medium text-foreground">{rule.rule_name}</span>
                      {rule.prompt_text && (
                        <span className="text-xs">- {rule.prompt_text.split('.')[0]}</span>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground mt-2">
                  View the shift's Associated Costs to see rule-generated items.
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setRuleNotification(null)}
              className="text-muted-foreground hover:text-foreground shrink-0 h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {!canCreateShift && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-sm p-4 mb-6 text-amber-500 text-sm">
            You need to create a project and location before adding shifts.
          </div>
        )}

        {/* EF-EMAIL-SUBJECT-ROUTER-V1 + UI-ZOOM-EXTENSION: Zoom Controls */}
        {/* EF-DATA-CLEANUP-AND-SHIFT-VISIBILITY-V1: Added Hide Past Shifts toggle */}
        {shifts.length > 0 && (
          <div className="flex items-center justify-end gap-4 mb-4">
            {/* Hide Past Shifts Toggle */}
            <div className="flex items-center gap-2" data-testid="hide-past-shifts-toggle">
              <Checkbox
                id="hide-past-shifts-list"
                checked={hidePastShifts}
                onCheckedChange={handleToggleHidePastShifts}
              />
              <label
                htmlFor="hide-past-shifts-list"
                className="text-sm text-muted-foreground cursor-pointer select-none flex items-center gap-1.5"
              >
                {hidePastShifts ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                Hide Past Shifts
              </label>
            </div>
            
            <div className="w-px h-6 bg-border" />
            
            <ZoomControl
              currentLabel={currentZoom.label}
              onZoomIn={zoomIn}
              onZoomOut={zoomOut}
              canZoomIn={canZoomIn}
              canZoomOut={canZoomOut}
              wrapText={wrapText}
              onToggleWrap={toggleWrap}
            />
          </div>
        )}

        <div className="bg-card border border-white/5 rounded-sm">
          {visibleShifts.length === 0 ? (
            <EmptyState
              icon={Clock}
              title={hidePastShifts && shifts.length > 0 ? "No upcoming shifts" : "No shifts yet"}
              description={
                hidePastShifts && shifts.length > 0 
                  ? "All shifts are in the past. Uncheck 'Hide Past Shifts' to see them."
                  : "Shifts are the core operational unit. Create one to start scheduling crew."
              }
              action={
                canCreateShift && (
                  <Button
                    onClick={() => handleOpenDialog()}
                    className="bg-primary text-primary-foreground"
                    data-testid="empty-add-shift-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Shift
                  </Button>
                )
              }
            />
          ) : (
            <div 
              className={`${!wrapText ? '[&_td]:whitespace-nowrap [&_td]:overflow-hidden [&_td]:text-ellipsis' : ''}`}
            >
              {/* EF-ENFORCE-SERVER-SIDE-SORT-FILTER-PLATFORM-WIDE-V1: Server-side sorting */}
              <DataTable 
                columns={columns} 
                data={visibleShifts}
                onSort={handleServerSort}
                sortField={sortField}
                sortDirection={sortDirection}
                disableLocalSort={true}
              />
              
              {/* Pagination info */}
              <div className="flex items-center justify-between p-4 border-t border-white/5">
                <div className="text-sm text-muted-foreground">
                  {shifts.length} of {totalCount} shifts
                  {hasMore && <span className="text-xs ml-1">(more available)</span>}
                </div>
                {hasMore && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadData(currentPage + 1, true)}
                  >
                    Load More
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </PageZoomContainer>

      {/* Create/Edit Dialog - Wider with scrollable content and sticky footer */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(next) => {
          // EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1
          if (next) { setDialogOpen(true); return; }
          if (selectedShift && shiftLineItemsRef.current?.hasChanges?.()) {
            setDiscardConfirmOpen(true);
            return;
          }
          setDialogOpen(false);
        }}
      >
        <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-border/50 shrink-0">
            <DialogTitle className="font-secondary text-xl uppercase tracking-wide">
              {selectedShift ? "Edit Shift" : "Add Shift"}
            </DialogTitle>
          </DialogHeader>
          
          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <form id="shift-form" onSubmit={handleSubmit} className="space-y-4">
              {/* Compact 2-column layout for shift details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="project_id" className="text-xs">Project *</Label>
                  <Select
                    value={formData.project_id}
                    onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                  >
                    <SelectTrigger data-testid="shift-project-select" className="h-8">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="venue_id" className="text-xs">Venue *</Label>
                  <Select
                    value={formData.venue_id}
                    onValueChange={(value) => setFormData({ 
                      ...formData, 
                      venue_id: value,
                      venue_location_id: "" // Reset location when venue changes
                    })}
                  >
                    <SelectTrigger data-testid="shift-venue-select" className="h-8">
                      <SelectValue placeholder="Select venue" />
                    </SelectTrigger>
                    <SelectContent>
                      {venues.map((venue) => (
                        <SelectItem key={venue.id} value={venue.id}>
                          {venue.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Location - cascading from venue */}
              <div>
                <Label htmlFor="venue_location_id" className="text-xs">Location *</Label>
                <Select
                  value={formData.venue_location_id}
                  onValueChange={(value) => setFormData({ ...formData, venue_location_id: value })}
                  disabled={!formData.venue_id}
                >
                  <SelectTrigger data-testid="shift-venue-location-select" className={`h-8 ${!formData.venue_id ? 'opacity-50' : ''}`}>
                    <SelectValue placeholder={formData.venue_id ? "Select location" : "Select venue first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {getFilteredVenueLocations(formData.venue_id).map((vl) => (
                      <SelectItem key={vl.id} value={vl.id}>
                        {vl.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-xs">Shift Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="h-8"
                    data-testid="shift-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="date" className="text-xs">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    className="h-8"
                    data-testid="shift-date-input"
                  />
                </div>
              </div>

              {/* Time fields - 4 columns */}
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label htmlFor="start_time" className="text-xs">Start *</Label>
                  <Input
                    id="start_time"
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    required
                    className="h-8"
                    data-testid="shift-start-time-input"
                  />
                </div>
                <div>
                  <Label htmlFor="duration" className="text-xs flex items-center gap-1">
                    Duration *
                    {isDurationCalculated && (
                      <Badge variant="outline" className="text-[8px] px-1 py-0 h-3.5 bg-primary/10 text-primary border-primary/30">
                        <Sparkles className="h-2 w-2 mr-0.5" />
                        auto
                      </Badge>
                    )}
                  </Label>
                  <Input
                    id="duration"
                    type="time"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    required
                    className={cn("h-8", isDurationCalculated && "bg-primary/5 border-primary/30")}
                    data-testid="shift-duration-input"
                  />
                </div>
                <div>
                  <Label htmlFor="end_time" className="text-xs flex items-center gap-1">
                    End *
                    {isEndTimeCalculated && (
                      <Badge variant="outline" className="text-[8px] px-1 py-0 h-3.5 bg-primary/10 text-primary border-primary/30">
                        <Sparkles className="h-2 w-2 mr-0.5" />
                        auto
                      </Badge>
                    )}
                  </Label>
                  <Input
                    id="end_time"
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    required
                    className={cn("h-8", isEndTimeCalculated && "bg-primary/5 border-primary/30")}
                    data-testid="shift-end-time-input"
                  />
                </div>
                <div>
                  <Label htmlFor="staff_required" className="text-xs">Crew *</Label>
                  <Input
                    id="staff_required"
                    type="number"
                    min="1"
                    value={formData.staff_required}
                    onChange={(e) => setFormData({ ...formData, staff_required: e.target.value })}
                    required
                    className="h-8"
                    data-testid="shift-staff-required-input"
                  />
                </div>
              </div>

              {/* Status + Notes - 2 columns */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="status" className="text-xs">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="shift-status-select" className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="notes" className="text-xs">Notes</Label>
                  <Input
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes"
                    className="h-8"
                    data-testid="shift-notes-input"
                  />
                </div>
              </div>

              {/* Out of Town Toggle - Prominent Button Style */}
              <div className="flex items-center justify-between p-4 bg-surface-base rounded-lg border border-border/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <Label htmlFor="out_of_town" className="text-sm font-semibold text-foreground cursor-pointer">
                      Out of Town (OOT)
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Add travel supplements (crew time, distance, tolls)
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant={formData.out_of_town ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFormData({ ...formData, out_of_town: !formData.out_of_town })}
                  className={formData.out_of_town ? "bg-primary text-primary-foreground" : ""}
                  data-testid="shift-out-of-town-btn"
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  {formData.out_of_town ? "OOT Enabled" : "Enable OOT"}
                </Button>
              </div>
            </form>
            
            {/* Line Items Section - Only shown when editing */}
            {selectedShift && (
              <div className="border-t border-border/50 pt-4 mt-4">
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  Shift Costs
                </h3>
                <ShiftLineItems 
                  ref={shiftLineItemsRef}
                  shiftId={selectedShift.id}
                  crewCount={parseInt(formData.staff_required) || 1}
                  onTotalsChange={() => {}}
                />
              </div>
            )}
            
            {/* Shift Crew Assignment - Only shown when editing */}
            {selectedShift && (
              <div className="border-t border-border/50 pt-4 mt-4">
                <ShiftCrewAssignment 
                  shiftId={selectedShift.id} 
                  staffRequired={parseInt(formData.staff_required) || 0}
                />
              </div>
            )}
          </div>

          {/* Sticky footer - always visible */}
          <div className="shrink-0 px-6 py-4 border-t border-border/50 bg-card flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                // EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1
                if (selectedShift && shiftLineItemsRef.current?.hasChanges?.()) {
                  setDiscardConfirmOpen(true);
                  return;
                }
                setDialogOpen(false);
              }}
              disabled={submitting}
              data-testid="shift-dialog-cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              form="shift-form"
              className="bg-primary text-primary-foreground" 
              data-testid="shift-submit-btn" 
              disabled={submitting}
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {selectedShift ? "Update" : "Create"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <DestructiveConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Shift"
        itemName={selectedShift?.name}
        itemType="shift"
        description={`Are you sure you want to delete shift "${selectedShift?.name}"? This will also remove any associated assignments. This action is permanent and cannot be undone.`}
        onConfirm={handleDelete}
        loading={deleting}
        requireReason={true}
        reasonMinLength={10}
      />

      {/* Rule Prompt Dialog */}
      <RulePromptDialog
        open={rulePromptOpen}
        onOpenChange={setRulePromptOpen}
        entityType="shift"
        entity={pendingShift}
        rulePrompts={rulePrompts}
        onAcceptAll={handleRuleAcceptAll}
        onOverride={handleRuleOverride}
        onCancel={handleRuleCancel}
        loading={ruleLoading}
      />

      {/* EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1 */}
      <AlertDialog
        open={discardConfirmOpen}
        onOpenChange={(o) => { if (!o && !discardInProgress) setDiscardConfirmOpen(false); }}
      >
        <AlertDialogContent
          className="bg-card border-white/10 max-w-md"
          data-testid="shift-discard-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription className="text-xs leading-relaxed">
              You changed{" "}
              <strong>
                {shiftLineItemsRef.current?.pendingChangeCount?.() ?? 0}
              </strong>{" "}
              supplement/line-item{" "}
              {((shiftLineItemsRef.current?.pendingChangeCount?.() ?? 0) === 1
                ? "row"
                : "rows")}{" "}
              in this session. Closing without saving will restore the
              original supplements exactly as they were before you opened
              this shift.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={discardInProgress}
              data-testid="shift-discard-cancel-btn"
            >
              Continue editing
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={discardInProgress}
              onClick={async (e) => {
                e.preventDefault();
                if (!shiftLineItemsRef.current?.discard) {
                  setDiscardConfirmOpen(false);
                  setDialogOpen(false);
                  return;
                }
                setDiscardInProgress(true);
                try {
                  const { restored, failures } =
                    await shiftLineItemsRef.current.discard();
                  if (failures > 0) {
                    toast.error(
                      `Restored ${restored} change(s) — ${failures} could not be reverted automatically.`,
                    );
                  } else {
                    toast.success(`Discarded ${restored} unsaved change(s).`);
                  }
                  loadData();
                } catch {
                  toast.error("Could not restore original supplements.");
                } finally {
                  setDiscardInProgress(false);
                  setDiscardConfirmOpen(false);
                  setDialogOpen(false);
                }
              }}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="shift-discard-confirm-btn"
            >
              {discardInProgress ? "Restoring..." : "Discard & restore"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
